import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import './DashboardLayout.css';
// Le thème privé englobe le cadre, les pages et leurs fenêtres Ant Design.
import PrivateUIProvider from '../common/PrivateUIProvider';
import { signOut, supabase } from '../../utils/supabase';
import PlanBadge from '../common/PlanBadge';
import ShopSwitcher from '../shops/ShopSwitcher';
import UserMenu from './UserMenu';
import { shopsApi } from '../../utils/api';
import { planService } from '../../services/planService';
import { setDeviseActive } from '../../utils/devise';
// Helper partagé avec ShopSwitcher pour les avatars d'initiales
import { obtenirInitiales } from '../../utils/initiales';
import {
  HomeIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ArrowLeftOnRectangleIcon,
  DocumentIcon,
  BellIcon,
  ShoppingBagIcon,
  TruckIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  CalculatorIcon,
  BuildingStorefrontIcon,
  UserCircleIcon,
  LockClosedIcon,
  Squares2X2Icon,
  TagIcon,
  StarIcon,
  WalletIcon,
  MegaphoneIcon,
  SparklesIcon,
  QueueListIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * Structure de navigation organisée en sections.
 * - `title` : libellé de la section (null = section sans en-tête)
 * - `items` : liens simples `{ name, href, icon }` ou groupes dépliables
 *   `{ name, icon, children: [...] }` rendus sous forme de dropdown.
 * Cette structure est générique : ajouter une section ou un dropdown ne
 * nécessite aucune modification du rendu.
 */
const navigationSections = [
  {
    title: null,
    items: [
      { name: 'Tableau de bord', href: '/app/dashboard', icon: HomeIcon },
    ],
  },
  {
    title: 'Ventes',
    items: [
      { name: 'Commandes', href: '/app/orders', icon: ShoppingBagIcon, roles: ['owner', 'admin', 'seller'] },
      {
        // Regroupe tout ce qui touche à la relation client
        name: 'Clients',
        icon: UserGroupIcon,
        children: [
          { name: 'Clients', href: '/app/clients', icon: UserGroupIcon },
          { name: 'Avis clients', href: '/app/feedbacks', icon: StarIcon },
          { name: 'Clientes Bloquées', href: '/app/unresponsive-customers', icon: ChatBubbleLeftRightIcon },
        ],
      },
    ],
  },
  {
    title: 'Approvisionnement',
    items: [
      {
        // Catalogue produits et promotions associées
        name: 'Produits',
        icon: ShoppingBagIcon,
        children: [
          { name: 'Catalogue', href: '/app/products', icon: ShoppingBagIcon },
          // { name: 'Bundles', href: '/app/bundles', icon: Squares2X2Icon },
          // Ajout de la page Réductions pour piloter les promotions
          { name: 'Réductions', href: '/app/discounts', icon: TagIcon },
        ],
      },
    ],
  },
  {
    title: 'Opérations',
    items: [
      // Équipe de la boutique sélectionnée : la route résout son identifiant actif.
      { name: 'Utilisateurs', href: '/app/users', icon: UserGroupIcon, roles: ['owner', 'admin'] },
      { name: 'Livreurs', href: '/app/delivery-persons', icon: TruckIcon, roles: ['owner', 'admin'] },
      //{ name: 'Attribution commandes', href: '/app/delivery-persons-orders', icon: ClipboardDocumentCheckIcon },
      { name: 'Caissiers', href: '/app/cashiers', icon: CalculatorIcon, roles: ['owner', 'admin'] },
      { name: 'Points de vente', href: '/app/locations', icon: BuildingStorefrontIcon, roles: ['owner', 'admin'] },
    ],
  },
  {
    title: 'Pilotage',
    items: [
      {
        // Indicateurs et suivi financier
        name: 'Rapports',
        icon: ChartBarIcon,
        roles: ['owner', 'admin'],
        children: [
          { name: 'Analytiques', href: '/app/analytics', icon: ChartBarIcon },
          { name: 'Dépenses', href: '/app/expenses', icon: WalletIcon },
        ],
      },
    ],
  },
  {
    title: 'WhatsApp IA',
    roles: ['owner', 'admin'],
    items: [
      { name: 'Assistant IA', href: '/app/assistant', icon: SparklesIcon },
      { name: 'File d’envoi', href: '/app/whatsapp-queue', icon: QueueListIcon },
      // Création de publicités Meta assistée par IA
      { name: 'Publicités Meta', href: '/app/meta-ads', icon: MegaphoneIcon },
      { name: 'Templates', href: '/app/templates', icon: DocumentIcon },
      { name: 'Notifications', href: '/app/notifications', icon: BellIcon },
    ],
  },
];

/**
 * Aplatit la structure en sections pour obtenir la liste de tous les liens
 * réels (utilisée pour la vérification des accès par plan).
 */
const navigation = navigationSections.flatMap((section) =>
  section.items.flatMap((item) => (item.children ? item.children : [item]))
);

/**
 * Taille d'icône unique pour toute la navigation (18px).
 * Réduite de 20px à 18px pour s'accorder aux libellés en 13px et alléger
 * visuellement la barre latérale.
 */
const CLASSES_ICONE = 'dashboard-nav-icon h-[18px] w-[18px] flex-shrink-0';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuAccess, setMenuAccess] = useState({});
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  // État d'ouverture des groupes dépliables du menu, indexé par nom de groupe
  const [openGroups, setOpenGroups] = useState({});
  // Conserve le choix d'affichage du menu desktop entre le mode complet et le mode icônes.
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [isShopCheckLoading, setIsShopCheckLoading] = useState(true);
  const [hasActiveShopAccess, setHasActiveShopAccess] = useState(false);
  const [activeShopRole, setActiveShopRole] = useState('');

  /**
   * Vérifie qu'une boutique active existe avant de rendre la navigation ou les
   * pages métier. En cas d'absence ou d'accès retiré, la session est redirigée
   * vers la page autonome de sélection, sans afficher le dashboard en arrière-plan.
   */
  useEffect(() => {
    const verifyActiveShop = async () => {
      const savedShopId = window.localStorage.getItem('activeShopId');
      if (!savedShopId) {
        setHasActiveShopAccess(false);
        setIsShopCheckLoading(false);
        return;
      }

      try {
        const response = await shopsApi.getAll({ is_active: true });
        const activeShop = (response.data || []).find((shop) => shop.id === savedShopId);
        const isAccessible = Boolean(activeShop);
        setActiveShopRole(activeShop?.role || '');
        // Mémorise la devise de la boutique active pour l'affichage des montants
        if (activeShop) setDeviseActive(activeShop.currency);
        if (!isAccessible) window.localStorage.removeItem('activeShopId');
        setHasActiveShopAccess(isAccessible);
      } catch (error) {
        console.error('Erreur lors de la vérification de la boutique active:', error);
        window.localStorage.removeItem('activeShopId');
        setHasActiveShopAccess(false);
      } finally {
        setIsShopCheckLoading(false);
      }
    };

    verifyActiveShop();
  }, []);

  // Ouvrir automatiquement le groupe contenant la page actuellement affichée
  useEffect(() => {
    const groupesAOuvrir = {};

    navigationSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children?.some((enfant) => location.pathname === enfant.href)) {
          groupesAOuvrir[item.name] = true;
        }
      });
    });

    // Fusion pour conserver les groupes ouverts manuellement par l'utilisateur
    setOpenGroups((precedent) => ({ ...precedent, ...groupesAOuvrir }));
  }, [location.pathname]);

  // Inverse l'état ouvert/fermé d'un groupe du menu
  const toggleGroup = (nomGroupe) => {
    setOpenGroups((precedent) => ({ ...precedent, [nomGroupe]: !precedent[nomGroupe] }));
  };

  /**
   * Bascule l'affichage desktop entre le menu complet et le menu réduit.
   * Le menu mobile conserve son comportement indépendant afin de ne pas
   * modifier l'expérience sur les petits écrans.
   */
  const toggleDesktopSidebar = () => {
    setIsDesktopSidebarCollapsed((precedent) => !precedent);
  };

  // Gérer le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Nettoyer au démontage
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Récupérer les informations utilisateur
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Récupérer le nom depuis user_settings
        const { data: userSettings } = await supabase
          .from('user_settings')
          .select('full_name')
          .eq('user_id', user.id)
          .single();

        setUserInfo({
          name: userSettings?.full_name || user.user_metadata?.full_name || '',
          email: user.email || ''
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // Vérifier les accès pour tous les éléments de menu
  useEffect(() => {
    const checkMenuAccess = async () => {
      const accessMap = {};
      
      // Vérifier l'accès pour chaque élément de navigation
      for (const item of navigation) {
        try {
          const hasAccess = await planService.canAccessRoute(item.href);
          accessMap[item.href] = hasAccess;
        } catch (error) {
          console.error(`Erreur lors de la vérification d'accès pour ${item.href}:`, error);
          accessMap[item.href] = true; // Par défaut, autoriser en cas d'erreur
        }
      }
      
      setMenuAccess(accessMap);
    };

    checkMenuAccess();
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Fonction pour rendre un élément de menu mobile avec gestion des accès
  const renderMobileMenuItem = (item, isActive) => {
    const hasAccess = menuAccess[item.href];

    // Cible tactile de 40px et texte 14px : plus compact tout en restant confortable au doigt.
    const baseClasses = 'dashboard-focusable flex min-h-10 items-center gap-2.5 px-3 py-2 text-sm font-medium';

    // Si l'utilisateur n'a pas accès, afficher en désactivé avec icône de verrouillage
    if (hasAccess === false) {
      return (
        <div
          key={item.name}
          className={`${baseClasses} dashboard-nav-locked`}
          title={`${item.name} - Nécessite un plan supérieur`}
        >
          <LockClosedIcon aria-hidden="true" className={CLASSES_ICONE} />
          <span className="truncate">{item.name}</span>
          <span className="dashboard-badge ml-auto flex-shrink-0">Premium</span>
        </div>
      );
    }

    // Accès accordé ou pas encore vérifié : rendu normal
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`${baseClasses} ${isActive ? 'dashboard-nav-active' : 'dashboard-nav-item'}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <item.icon aria-hidden="true" className={CLASSES_ICONE} />
        <span className="truncate">{item.name}</span>
      </Link>
    );
  };

  // Fonction pour rendre un élément de menu avec gestion des accès
  const renderMenuItem = (item, isActive) => {
    const hasAccess = menuAccess[item.href];

    /**
     * Densité desktop : lignes de 32px et texte 13px.
     * Le pointeur ne nécessite pas la cible tactile mobile, et la barre
     * latérale accueille ainsi l'ensemble des sections sans défilement.
     */
    const classesElement = `dashboard-focusable flex items-center text-[13px] font-medium ${
      isDesktopSidebarCollapsed
        ? 'mx-auto h-9 w-9 justify-center'
        : 'min-h-8 gap-2 px-2 py-1.5'
    }`;

    // Si l'utilisateur n'a pas accès, afficher en désactivé avec icône de verrouillage
    if (hasAccess === false) {
      return (
        <div
          key={item.name}
          title={`${item.name} - Nécessite un plan supérieur`}
          className={`${classesElement} dashboard-nav-locked`}
        >
          <LockClosedIcon aria-hidden="true" className={CLASSES_ICONE} />
          <span className={isDesktopSidebarCollapsed ? 'sr-only' : 'truncate'}>{item.name}</span>
          <span className={isDesktopSidebarCollapsed ? 'sr-only' : 'dashboard-badge ml-auto flex-shrink-0'}>
            Premium
          </span>
        </div>
      );
    }

    // Accès accordé ou pas encore vérifié : rendu normal
    return (
      <Link
        key={item.name}
        to={item.href}
        title={isDesktopSidebarCollapsed ? item.name : undefined}
        className={`${classesElement} ${isActive ? 'dashboard-nav-active' : 'dashboard-nav-item'}`}
      >
        <item.icon aria-hidden="true" className={CLASSES_ICONE} />
        <span className={isDesktopSidebarCollapsed ? 'sr-only' : 'truncate'}>{item.name}</span>
      </Link>
    );
  };

  /**
   * Rend un groupe dépliable (dropdown) : un bouton parent qui ouvre/ferme
   * la liste de ses sous-éléments. Le parent est mis en évidence lorsqu'une
   * de ses pages est active et que le groupe est replié.
   * @param {Object} group - Élément de navigation possédant `children`
   * @param {boolean} isMobile - Adapte les tailles au menu mobile
   */
  const renderNavGroup = (group, isMobile) => {
    const estOuvert = !!openGroups[group.name];
    const contientPageActive = group.children.some((enfant) => location.pathname === enfant.href);
    const rendreEnfant = isMobile ? renderMobileMenuItem : renderMenuItem;
    const estCompact = !isMobile && isDesktopSidebarCollapsed;
    // Identifiant stable permettant d'associer le bouton au contenu qu'il contrôle.
    const identifiantSousMenu = `sous-menu-${group.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

    return (
      <div key={group.name}>
        <button
          type="button"
          onClick={() => toggleGroup(group.name)}
          title={estCompact ? group.name : undefined}
          className={`dashboard-focusable flex w-full items-center font-medium ${
            estCompact
              ? 'mx-auto h-9 w-9 justify-center'
              : isMobile
                ? 'min-h-10 gap-2.5 px-3 py-2 text-sm'
                : 'min-h-8 gap-2 px-2 py-1.5 text-[13px]'
          } ${
            contientPageActive && !estOuvert
              ? 'dashboard-nav-active'
              : 'dashboard-nav-item'
          }`}
          aria-expanded={estOuvert}
          aria-controls={identifiantSousMenu}
        >
          <group.icon aria-hidden="true" className={CLASSES_ICONE} />
          <span className={estCompact ? 'sr-only' : 'truncate'}>{group.name}</span>
          {/* Chevron pivotant indiquant l'état du dropdown */}
          {!estCompact && (
            <ChevronDownIcon
              aria-hidden="true"
              className={`dashboard-nav-icon ml-auto h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 motion-reduce:transition-none ${
                estOuvert ? 'rotate-180' : ''
              }`}
            />
          )}
        </button>

        {/* Sous-éléments affichés uniquement lorsque le groupe est ouvert */}
        {estOuvert && (
          <div
            id={identifiantSousMenu}
            className={
              estCompact
                ? 'dashboard-submenu-compact mt-1 space-y-1 pt-1'
                : `dashboard-submenu mt-0.5 pl-1.5 ${isMobile ? 'ml-5 space-y-0.5' : 'ml-3.5 space-y-px'}`
            }
          >
            {group.children.map((enfant) =>
              rendreEnfant(enfant, location.pathname === enfant.href)
            )}
          </div>
        )}
      </div>
    );
  };

  /**
   * Rend l'ensemble de la navigation : sections titrées contenant des liens
   * simples ou des groupes dépliables.
   * @param {boolean} isMobile - Adapte les tailles au menu mobile
   */
  const renderNavigation = (isMobile) => {
    const rendreLien = isMobile ? renderMobileMenuItem : renderMenuItem;
    const estCompact = !isMobile && isDesktopSidebarCollapsed;

    /**
     * Séparation des sections : le titre en capitales suffit à marquer la
     * rupture, le filet horizontal n'est donc conservé que dans le mode réduit,
     * où les titres ne sont plus affichés.
     */
    const classesSection = (index) => {
      if (index === 0) return '';
      return estCompact ? 'dashboard-divider mt-2 border-t pt-2' : 'mt-3';
    };

    return navigationSections.filter((section) => !section.roles || section.roles.includes(activeShopRole)).map((section, index) => (
      <div key={section.title || `section-${index}`} className={classesSection(index)}>
        {/* En-tête de section (optionnel, masqué en mode réduit) */}
        {section.title && !estCompact && (
          <h3 className="dashboard-section-title">{section.title}</h3>
        )}
        <div className={isMobile ? 'space-y-0.5' : 'space-y-px'}>
          {section.items.filter((item) => !item.roles || item.roles.includes(activeShopRole)).map((item) =>
            item.children
              ? renderNavGroup(item, isMobile)
              : rendreLien(item, location.pathname === item.href)
          )}
        </div>
      </div>
    ));
  };

  /**
   * Entrées du menu utilisateur, groupées par sections séparées d'un filet.
   * Décrites en données pour que `UserMenu` reste générique.
   */
  const sectionsMenuUtilisateur = [
    [
      { label: 'Mon profil', href: '/app/profile', icon: UserCircleIcon },
      { label: 'Paramètres', href: '/app/settings', icon: Cog6ToothIcon },
    ],
    [
      { label: 'Se déconnecter', onClick: handleSignOut, icon: ArrowLeftOnRectangleIcon, danger: true },
    ],
  ];

  /**
   * Pied de menu commun (desktop et mobile) : avatar, nom et plan (à la place
   * de l'e-mail) ouvrant un menu déroulant Profil / Paramètres / Déconnexion.
   * Le plan n'est pas cliquable ici (`showUpgradeLink={false}`) pour ne pas
   * imbriquer un lien dans le bouton déclencheur.
   * @param {boolean} estCompact - Mode réduit : seul l'avatar est affiché
   */
  const renderUserFooter = (estCompact) => (
    <div className={`dashboard-divider dashboard-bar flex-shrink-0 border-t ${estCompact ? 'p-2' : 'px-2 py-2'}`}>
      <UserMenu
        nom={userInfo.name || userInfo.email}
        email={userInfo.email}
        initiales={obtenirInitiales(userInfo.name, userInfo.email)}
        sousTitre={<PlanBadge variant="inline" showProgress={true} showUpgradeLink={false} />}
        sections={sectionsMenuUtilisateur}
        compact={estCompact}
        onNavigate={() => setIsMobileMenuOpen(false)}
      />
    </div>
  );

  if (isShopCheckLoading) {
    // Indicateur neutre de la charte ; la vérification de boutique conserve son fonctionnement.
    return <div className="dashboard-layout-loading flex min-h-dvh items-center justify-center" aria-label="Vérification de la boutique active"><div className="dashboard-layout-spinner h-10 w-10 animate-spin rounded-full border-2" /></div>;
  }

  if (!hasActiveShopAccess) return <Navigate to="/app/select-shop" replace />;

  return (
    <PrivateUIProvider>
    <div className="dashboard-layout min-h-dvh">
      {/* Lien d'évitement : permet aux utilisateurs clavier d'atteindre directement le contenu. */}
      <a
        href="#contenu-principal"
        className="dashboard-skip-link dashboard-focusable sr-only z-[60] focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Aller au contenu principal
      </a>

      {/* Navigation flottante : 16px de marge et 72px de panneau dans le mode réduit de 88px. */}
      <aside aria-label="Navigation principale" className={`dashboard-desktop-sidebar hidden md:fixed md:inset-y-0 md:z-30 md:flex md:flex-col transition-[width] duration-200 motion-reduce:transition-none ${
        isDesktopSidebarCollapsed ? 'md:w-[88px]' : 'md:w-64'
      }`}>
        {/*
          Structure en trois zones : l'en-tête et le pied restent fixes, seule la
          navigation défile. Auparavant le logo, la carte d'identité et le bouton
          de déconnexion disparaissaient au défilement sur les écrans courts.
        */}
        <div className="dashboard-sidebar flex min-h-0 flex-1 flex-col">

          {/* --- Zone fixe : identité de la boutique et bouton de réduction --- */}
          <div className={`flex flex-shrink-0 pt-3 ${
            isDesktopSidebarCollapsed ? 'flex-col items-center gap-2 px-2' : 'items-center justify-between px-3'
          }`}>
            {/* Le carré du logo reste visible en mode réduit : il ancre l'identité */}
            <div className="flex min-w-0 items-center gap-2">
              <div className="dashboard-avatar flex h-8 w-8 flex-shrink-0 items-center justify-center">
                <ShoppingBagIcon aria-hidden="true" className="h-4 w-4" />
              </div>
              {/* Nom de l'application, aligné sur la même ligne que le logo */}
              {!isDesktopSidebarCollapsed && (
                <p className="dashboard-strong truncate text-sm font-semibold tracking-tight">Mercuri Shop</p>
              )}
            </div>
            <button
              type="button"
              onClick={toggleDesktopSidebar}
              className="dashboard-icon-btn dashboard-focusable flex h-8 w-8 flex-shrink-0 items-center justify-center"
              title={isDesktopSidebarCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
              aria-label={isDesktopSidebarCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
            >
              {isDesktopSidebarCollapsed
                ? <ChevronRightIcon aria-hidden="true" className="h-4 w-4" />
                : <ChevronLeftIcon aria-hidden="true" className="h-4 w-4" />}
            </button>
          </div>

          {/*
            --- Zone fixe : boutique active (mode complet) ---
            La carte utilisateur a été déplacée dans le pied de menu pour
            libérer le haut de la barre latérale.
          */}
          {!isDesktopSidebarCollapsed && (
            <div className="dashboard-divider mt-3 flex-shrink-0 border-b px-3 pb-3">
              {/* Le contexte courant est toujours visible et peut être modifié sans quitter le menu. */}
              <ShopSwitcher />
            </div>
          )}

          {/* --- Zone défilante : navigation --- */}
          <nav className={`dashboard-scroll min-h-0 flex-1 overflow-y-auto py-3 ${
            isDesktopSidebarCollapsed ? 'px-2' : 'px-3'
          }`}>
            {renderNavigation(false)}
          </nav>

          {/* --- Zone fixe : utilisateur et déconnexion --- */}
          {renderUserFooter(isDesktopSidebarCollapsed)}
        </div>
      </aside>

      {/* Overlay pour menu mobile */}
      {isMobileMenuOpen && (
        <div
          className="dashboard-scrim fixed inset-0 z-40 backdrop-blur-[1px] transition-opacity ease-linear duration-200 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Menu mobile */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation principale"
        className={`dashboard-sidebar fixed top-0 left-0 z-50 h-full w-full max-w-[19rem] md:hidden transform transition-transform ease-in-out duration-200 motion-reduce:transition-none ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Contenu du menu mobile */}
        <div className="flex h-full flex-col pt-3">
          {/*
            En-tête : le bouton de fermeture est désormais à l'intérieur du panneau.
            Placé à l'extérieur (-mr-14), il débordait de la fenêtre sur un écran
            de 375px et provoquait un défilement horizontal.
          */}
          <div className="flex flex-shrink-0 items-center gap-2 px-4">
            <div className="dashboard-avatar flex h-8 w-8 flex-shrink-0 items-center justify-center">
              <ShoppingBagIcon aria-hidden="true" className="h-4 w-4" />
            </div>
            {/* Nom de l'application, aligné sur la même ligne que le logo */}
            <p className="dashboard-strong min-w-0 truncate text-sm font-semibold">Mercuri Shop</p>
            <button
              type="button"
              className="dashboard-icon-btn dashboard-focusable ml-auto flex h-10 w-10 flex-shrink-0 items-center justify-center"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="sr-only">Fermer le menu</span>
              <XMarkIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>

          {/* Boutique active - Mobile (plan sous le nom, utilisateur dans le pied) */}
          <div className="dashboard-divider mt-3 flex-shrink-0 border-b px-4 pb-3">
            <ShopSwitcher />
          </div>

          {/* Navigation - seule zone défilante du drawer */}
          <nav className="dashboard-scroll min-h-0 flex-1 overflow-y-auto px-3 py-3">
            {renderNavigation(true)}
          </nav>

          {/* Utilisateur et déconnexion - Mobile */}
          {renderUserFooter(false)}
        </div>
      </div>

      {/* Contenu privé : marge adaptée au panneau flottant et au mode réduit. */}
      <div className={`dashboard-content flex flex-1 flex-col transition-[padding] duration-200 motion-reduce:transition-none ${
        isDesktopSidebarCollapsed ? 'md:pl-[88px]' : 'md:pl-64'
      }`}>
        {/* Header avec badge du plan pour mobile */}
        {/* L'en-tête reste sous le voile du drawer pour que toute la page soit assombrie. */}
        <div className="dashboard-divider dashboard-bar sticky top-0 z-30 border-b md:hidden">
          <div className="flex items-center justify-between gap-3 px-3 py-2">
            <button
              className="dashboard-icon-btn dashboard-focusable inline-flex h-11 w-11 flex-shrink-0 items-center justify-center"
              onClick={() => setIsMobileMenuOpen(true)}
              type="button"
            >
              <span className="sr-only">Ouvrir le menu</span>
              <Bars3Icon aria-hidden="true" className="h-5 w-5" />
            </button>

            {/* Informations utilisateur et plan - Header mobile */}
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="max-w-40"><ShopSwitcher compact /></div>
              <div className="min-w-0 text-right">
                <div className="dashboard-strong truncate text-sm font-medium">
                  {userInfo.name || userInfo.email}
                </div>
                <PlanBadge variant="inline" showProgress={false} />
              </div>
              <div className="dashboard-avatar flex h-9 w-9 flex-shrink-0 items-center justify-center">
                {obtenirInitiales(userInfo.name, userInfo.email)}
              </div>
            </div>
          </div>
        </div>

        {/* Zone de rendu des pages : aucun style imposé aux pages métier */}
        <main id="contenu-principal" className="flex-1" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
    </PrivateUIProvider>
  );
};

export default DashboardLayout;
